import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { canonicalDishTypes, dishTypeFromSourceUrl } from '../utils/dishTypeVocabulary';
import { firestore, isFirebaseEnabled } from './firebaseConfig';
import { Recipe } from '../types/recipe';

export interface ImportedRecipe {
  id: string;
  title: string;
  description?: string;
  image?: string;
  prepTime?: string;
  cookTime?: string;
  totalTime?: string;
  recipeYield?: string;
  recipeCategory?: string;
  recipeCuisine?: string;
  ingredients: string[];
  instructions: string[];
  author?: string;
  keywords?: string;
  nutrition?: any;
  diets?: string[];
  sourceUrl: string;
  sourceName: string;
  importedAt: string;
  source: 'browser-extension';
  type: 'imported';
  userId: string;
}

/**
 * The source site's own classification, in canonical form. Prefers the
 * published category; falls back to a category segment in the URL path. Returns
 * [] rather than the raw string when neither is recognised, so an unclassified
 * recipe surfaces in the quality checker instead of reaching the planner as a
 * fake meal.
 */
function resolveDishTypes(imported: ImportedRecipe): string[] {
  const fromCategory = canonicalDishTypes(
    imported.recipeCategory ? [imported.recipeCategory] : []
  );
  if (fromCategory.length) return fromCategory;
  const fromUrl = dishTypeFromSourceUrl(imported.sourceUrl);
  return fromUrl ? [fromUrl] : [];
}

/**
 * Fetch imported recipes for a user
 */
export async function fetchImportedRecipes(
  userId: string,
  maxResults: number = 50
): Promise<Recipe[]> {
  if (!isFirebaseEnabled() || !firestore) {
    console.warn('Firebase not enabled, cannot fetch imported recipes');
    return [];
  }

  try {
    const recipesRef = collection(firestore, 'users', userId, 'importedRecipes');
    const q = query(recipesRef, orderBy('importedAt', 'desc'), limit(maxResults));
    const snapshot = await getDocs(q);

    const recipes: Recipe[] = snapshot.docs.map(doc => {
      const data = doc.data() as ImportedRecipe;

      // Convert imported recipe to standard Recipe format
      return convertImportedToRecipe(data);
    });

    console.log(`✅ Fetched ${recipes.length} imported recipes`);
    return recipes;
  } catch (error) {
    console.error('Failed to fetch imported recipes:', error);
    return [];
  }
}

/**
 * Convert imported recipe to standard Recipe format
 */
function convertImportedToRecipe(imported: ImportedRecipe): Recipe & { analyzedInstructions?: any[] } {
  return {
    id: hashRecipeId(imported.id), // Convert string ID to number
    title: imported.title,
    image: imported.image || '',
    servings: parseServings(imported.recipeYield),
    ...(imported.recipeYield && { servingsDisplay: imported.recipeYield }),
    // No fabricated default: a recipe with no real time yields 0, which the
    // display layer renders as blank (consistent with the meal-planner cards).
    readyInMinutes: parseTime(imported.totalTime) ||
      (parseTime(imported.prepTime) + parseTime(imported.cookTime)),
    sourceUrl: imported.sourceUrl,
    summary: imported.description || '',

    // Extended ingredients - convert simple strings to detailed format with parsed values
    extendedIngredients: imported.ingredients.map((ing, index) => {
      const amount = parseIngredientAmount(ing);
      const unit = parseIngredientUnit(ing);
      const name = parseIngredientName(ing);
      return {
        id: index,
        aisle: '',
        image: '',
        name: name || ing,  // Fall back to original if parsing fails
        original: ing,
        amount: amount,
        unit: unit,
        meta: [],
        measures: { us: { amount: amount, unitShort: unit, unitLong: unit }, metric: { amount: amount, unitShort: unit, unitLong: unit } }
      };
    }),

    // Instructions - flat format for recipe-detail screen
    // Normalize: Firestore may store instructions as RecipeInstruction[] rather than string[]
    instructions: (imported.instructions as any[]).map((inst, index) => {
      const stepText = typeof inst === 'string' ? inst : (inst?.step ?? String(inst ?? ''));
      const stepNum = typeof inst === 'object' && inst?.number ? inst.number : index + 1;
      return { number: stepNum, step: stepText };
    }),

    // Instructions - convert simple strings to detailed format
    analyzedInstructions: imported.instructions.length > 0 ? [{
      name: '',
      steps: (imported.instructions as any[]).map((inst, index) => {
        const stepText = typeof inst === 'string' ? inst : (inst?.step ?? String(inst ?? ''));
        const stepNum = typeof inst === 'object' && inst?.number ? inst.number : index + 1;
        return { number: stepNum, step: stepText, ingredients: [], equipment: [] };
      })
    }] : [],

    // Dietary info (not available from scraped recipes)
    cheap: false,
    sustainable: false,
    lowFodmap: false,

    // Weight watcher info
    weightWatcherSmartPoints: 0,

    // Gaps info
    gaps: '',

    // Preparation info
    preparationMinutes: parseTime(imported.prepTime),
    cookingMinutes: parseTime(imported.cookTime),

    // Aggregates
    aggregateLikes: 0,
    healthScore: 0,
    creditsText: imported.author || imported.sourceName,
    sourceName: imported.sourceName,
    pricePerServing: 0,

    // Categories
    // Normalise the source site's own wording ('Déjeuners et brunchs') into the
    // canonical taxonomy. Falls back to [] rather than the raw string, so an
    // unrecognised category surfaces in the quality checker instead of being
    // silently mislabelled — and never reaches the planner as a fake meal.
    dishTypes: resolveDishTypes(imported),
    diets: imported.diets || [],
    occasions: [],
    cuisines: imported.recipeCuisine ? [imported.recipeCuisine] : [],

    // Nutrition
    nutrition: imported.nutrition || undefined,

    // Mark as imported and store original Firestore ID for unsaving
    _imported: true,
    _importedAt: imported.importedAt,
    _firestoreId: imported.id // Store original ID for deleting from Firestore
  };
}

/**
 * Parse amount from an ingredient string.
 * Handles decimals, fractions, and ranges.
 * Examples: "1.5 oignon" -> 1.5, "1/2 cup milk" -> 0.5, "45 ml butter" -> 45
 */
function parseIngredientAmount(text: string): number {
  if (!text) return 0;

  // Look for decimal numbers first (e.g., 1.5, 0.25)
  const decimalMatch = text.match(/^[\s]*(\d+[.,]\d+)/);
  if (decimalMatch) {
    return parseFloat(decimalMatch[1].replace(',', '.'));
  }

  // Look for fractions (e.g., 1/2, 3/4)
  const fractionMatch = text.match(/^[\s]*(\d+)\s*\/\s*(\d+)/);
  if (fractionMatch) {
    return parseInt(fractionMatch[1]) / parseInt(fractionMatch[2]);
  }

  // Look for whole numbers
  const wholeMatch = text.match(/^[\s]*(\d+)/);
  if (wholeMatch) {
    return parseInt(wholeMatch[1]);
  }

  return 0;
}

/**
 * Parse unit from an ingredient string.
 * Handles common metric, imperial, and French units.
 */
function parseIngredientUnit(text: string): string {
  if (!text) return '';

  const normalized = text.toLowerCase();

  // Common units to look for (sorted by length to match longer ones first)
  const units = [
    // Metric volume
    'milliliters', 'millilitres', 'milliliter', 'millilitre', 'ml',
    'liters', 'litres', 'liter', 'litre', 'l',
    // Metric weight
    'kilogrammes', 'kilogramme', 'kilograms', 'kilogram', 'kg',
    'grammes', 'gramme', 'grams', 'gram', 'g',
    // Imperial volume
    'tablespoons', 'tablespoon', 'tbsp', 'tbs', 'tb',
    'teaspoons', 'teaspoon', 'tsp', 'ts',
    'cups', 'cup', 'c.',
    'fluid ounces', 'fluid ounce', 'fl oz', 'fl. oz.',
    'pints', 'pint', 'pt',
    'quarts', 'quart', 'qt',
    'gallons', 'gallon', 'gal',
    // Imperial weight
    'pounds', 'pound', 'lbs', 'lb',
    'ounces', 'ounce', 'oz',
    // French units
    'c. à soupe', 'c. à café', 'c. à thé',
    'cuillères à soupe', 'cuillère à soupe', 'cuillères à café', 'cuillère à café',
    'tasses', 'tasse',
    // Count-based
    'pieces', 'piece', 'pcs', 'pc',
    'slices', 'slice',
    'cloves', 'clove',
    'pinches', 'pinch',
    'bunches', 'bunch',
    'cans', 'can',
    'packages', 'package', 'pkg',
    'bottles', 'bottle',
    'jars', 'jar',
    // French count
    'gousses', 'gousse',
    'tranches', 'tranche',
    'pincées', 'pincée',
    'boîtes', 'boîte',
    'paquets', 'paquet',
  ];

  // Remove leading number from text to search for unit
  const textWithoutNumber = normalized.replace(/^[\d.,\s\/]+/, '');

  for (const unit of units) {
    // Check if the text starts with or contains the unit
    if (textWithoutNumber.startsWith(unit + ' ') ||
        textWithoutNumber.startsWith(unit + '\t') ||
        textWithoutNumber === unit ||
        // Also check with parentheses for format like "45 ml (2 c. à soupe)"
        normalized.includes(`${unit} `) ||
        normalized.includes(`${unit})`)) {
      return unit;
    }
  }

  return '';
}

/**
 * Extract the ingredient name from a full ingredient string.
 * Removes quantities, units, and common prefixes.
 */
function parseIngredientName(text: string): string {
  if (!text) return '';

  let result = text.trim();

  // Remove leading quantity (numbers, fractions, decimals)
  result = result.replace(/^[\d.,\s\/]+/, '').trim();

  // Remove parenthetical measurements like "(2 c. à soupe)"
  result = result.replace(/\([^)]*\)\s*/g, '').trim();

  // Common units to remove (order matters - longer first)
  const unitsToRemove = [
    'milliliters', 'millilitres', 'milliliter', 'millilitre', 'ml',
    'liters', 'litres', 'liter', 'litre',
    'kilogrammes', 'kilogramme', 'kilograms', 'kilogram', 'kg',
    'grammes', 'gramme', 'grams', 'gram', 'g',
    'tablespoons', 'tablespoon', 'tbsp', 'tbs', 'tb',
    'teaspoons', 'teaspoon', 'tsp', 'ts',
    'cups', 'cup', 'c\\.',
    'fluid ounces', 'fluid ounce', 'fl oz', 'fl\\. oz\\.',
    'pounds', 'pound', 'lbs', 'lb',
    'ounces', 'ounce', 'oz',
    'c\\. à soupe', 'c\\. à café', 'c\\. à thé',
    'cuillères à soupe', 'cuillère à soupe',
    'tasses', 'tasse',
    'gousses', 'gousse',
  ];

  for (const unit of unitsToRemove) {
    const regex = new RegExp(`^${unit}\\s+`, 'i');
    result = result.replace(regex, '').trim();
  }

  // Remove French prepositions: de, d', du, des
  result = result.replace(/^(de\s+|d'|du\s+|des\s+)/i, '').trim();

  return result || text;
}

/**
 * Hash string ID to number (for numeric ID compatibility).
 *
 * NOTE: This algorithm is duplicated in `src/services/saveRecipeService.ts` and
 * `api/_lib/recipeIdHash.ts` (server-side backfill). All three MUST stay
 * byte-for-byte identical — the backfill reverses this hash to recover canonical
 * ids, so any divergence breaks the repair. Change them together.
 */
export function hashRecipeId(id: string): number {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    const char = id.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  // Ensure positive and use imported- prefix range
  return Math.abs(hash) + 9000000; // Use high number range to avoid conflicts
}

/**
 * Parse servings from various formats
 * Returns first number for compatibility, but we should preserve full range
 */
function parseServings(yieldStr: string | undefined): number {
  if (!yieldStr) return 4; // Default

  // Extract first number (e.g., "4 to 6" -> 4, "6-8" -> 6)
  const match = yieldStr.match(/\d+/);
  return match ? parseInt(match[0]) : 4;
}

/**
 * Parse servings display text (preserves ranges like "4 to 6")
 */
function parseServingsDisplay(yieldStr: string | undefined): string {
  if (!yieldStr) return '4';

  // Keep the numeric part including ranges: "4 to 6 servings" -> "4 to 6"
  // Also handle: "4-6", "serves 4", "6 portions"
  return yieldStr;
}

/**
 * Parse ISO 8601 duration to minutes
 */
function parseTime(duration: string | undefined): number {
  if (!duration) return 0;

  // Parse ISO 8601 duration (e.g., "PT30M", "PT1H30M")
  const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
  if (!match) return 0;

  const hours = match[1] ? parseInt(match[1]) : 0;
  const minutes = match[2] ? parseInt(match[2]) : 0;

  return (hours * 60) + minutes;
}

/**
 * Parse total time - try totalTime first, then sum prep + cook
 */
function parseTotalTime(time: string | undefined): number {
  if (!time) return 30; // Default
  return parseTime(time) || 30;
}

export default {
  fetchImportedRecipes,
  convertImportedToRecipe
};
